import type { CurrentActor } from "../api/session-api";
import { permissionCodes as adminPermissions } from "../generated-api/permissions";

export const demoIdentities = [
  "maker",
  "moderator",
  "partner-manager",
  "reviewer",
  "admin-maker",
] as const;
export type DemoIdentity = (typeof demoIdentities)[number];
export function isDemoIdentity(value: string | null): value is DemoIdentity {
  return value !== null && demoIdentities.includes(value as DemoIdentity);
}
export const demoIdentityKey = "cyra.demo.identity";
export const demoRolePermissions = {
  ADMIN: [...adminPermissions],
  CONTENT_EDITOR: [
    "admin.access",
    "approvals.read",
    "destinations.read",
    "experiences.manage",
    "experiences.read",
    "legal.manage",
    "notifications.read",
    "partner-offerings.manage",
    "partner-offerings.read",
    "partner-schedules.manage",
    "partner-schedules.read",
    "partner.import",
    "partners.manage",
    "partners.read",
  ],
  MODERATOR: [
    "admin.access",
    "approvals.decide",
    "approvals.read",
    "audit.read",
    "destinations.read",
    "experiences.read",
    "legal.acceptance.read",
    "notifications.read",
    "partner-offerings.read",
    "partner-schedules.read",
    "partners.read",
    "sessions.read",
    "sessions.revoke",
    "users.access.manage",
    "users.read",
  ],
  PARTNER_CONTENT_MANAGER: [
    "admin.access",
    "approvals.read",
    "destinations.read",
    "experiences.read",
    "notifications.read",
    "partner-offerings.manage",
    "partner-offerings.read",
    "partner-schedules.manage",
    "partner-schedules.read",
    "partners.read",
  ],
  USER: ["user.access"],
} satisfies Record<
  "ADMIN" | "CONTENT_EDITOR" | "MODERATOR" | "PARTNER_CONTENT_MANAGER" | "USER",
  CurrentActor["permissions"]
>;

export function demoActor(identity: DemoIdentity): CurrentActor {
  const moderator = identity === "moderator";
  const reviewer = identity === "reviewer" || identity === "admin-maker";
  const partnerManager = identity === "partner-manager";
  return {
    authorityEpoch: 1,
    catalogueScope: partnerManager
      ? {
          kind: "PARTNER_EXPERIENCE_SET",
          grants: [
            {
              destinationId: "destination-lima",
              experienceIds: ["experience-barranco-art"],
              partnerId: "partner-barranco-local",
            },
          ],
        }
      : { kind: "PLATFORM_ALL" },
    email: `${identity}@example.invalid`,
    firstName: "Synthetic",
    lastName:
      identity === "admin-maker"
        ? "Admin Maker"
        : partnerManager
          ? "Partner Manager"
          : moderator
            ? "Moderator"
            : reviewer
              ? "Reviewer"
              : "Maker",
    permissions: partnerManager
      ? [...demoRolePermissions.PARTNER_CONTENT_MANAGER]
      : moderator
        ? [...demoRolePermissions.MODERATOR]
        : reviewer
          ? [...demoRolePermissions.ADMIN]
          : [...demoRolePermissions.CONTENT_EDITOR],
    profileImageAssetId: null,
    roles: partnerManager
      ? ["USER", "PARTNER_CONTENT_MANAGER"]
      : moderator
        ? ["USER", "MODERATOR"]
        : reviewer
          ? ["USER", "ADMIN"]
          : ["USER", "CONTENT_EDITOR"],
    sessionId: partnerManager
      ? "ses_DEMO_PARTNER_MANAGER"
      : identity === "admin-maker"
        ? "ses_DEMO_ADMIN_MAKER"
        : moderator
          ? "ses_DEMO_MODERATOR"
          : reviewer
            ? "ses_DEMO_REVIEWER"
            : "ses_DEMO_MAKER",
    userId: partnerManager
      ? "usr_partner_ana"
      : identity === "admin-maker"
        ? "usr_DEMO_ADMIN_MAKER"
        : moderator
          ? "usr_DEMO_MODERATOR"
          : reviewer
            ? "usr_01ADMIN"
            : "usr_DEMO_MAKER",
  };
}
export function selectedDemoActor(): CurrentActor {
  const identity = window.localStorage.getItem(demoIdentityKey);
  return demoActor(isDemoIdentity(identity) ? identity : "reviewer");
}
